// Profile.jsx - Profile page component

import React, { useState } from "react";
import { useSelector } from "react-redux";
import Button from "../components/common/Button";
import UpdatePasswordModal from "../components/UI/UpdatePasswordModal";
import ThemeToggle from "../components/UI/ThemeToggle";
import {
  UserCircleIcon,
  MailIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/outline";

const Profile = () => {
  const { user } = useSelector((state) => state.auth);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const openPasswordModal = () => setIsPasswordModalOpen(true);
  const closePasswordModal = () => setIsPasswordModalOpen(false);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary to-primary-dark p-6 text-white">
          <div className="flex items-center space-x-4">
            <div className="bg-white bg-opacity-20 p-4 rounded-full">
              <UserCircleIcon className="w-16 h-16" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{user.name}</h1>
              <p className="text-white text-opacity-90">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6">
          {/* Theme Toggle */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Appearance
            </h2>
            <ThemeToggle />
          </div>

          {/* Profile Details */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Profile Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="flex items-center text-sm font-medium text-gray-600 mb-2">
                  <UserCircleIcon className="w-5 h-5 mr-2" />
                  Full Name
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {user.name}
                </p>
              </div>

              {/* Email */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="flex items-center text-sm font-medium text-gray-600 mb-2">
                  <MailIcon className="w-5 h-5 mr-2" />
                  Email Address
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {user.email}
                </p>
              </div>

              {/* Phone */}
              {user.phoneNumber && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="flex items-center text-sm font-medium text-gray-600 mb-2">
                    <PhoneIcon className="w-5 h-5 mr-2" />
                    Phone Number
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {user.phoneNumber}
                  </p>
                </div>
              )}

              {/* Role */}
              {user.role && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="flex items-center text-sm font-medium text-gray-600 mb-2">
                    <ShieldCheckIcon className="w-5 h-5 mr-2" />
                    Role
                  </label>
                  <p className="text-lg font-semibold text-gray-900 capitalize">
                    {user.role}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Security Section */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Security
            </h2>
            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Password</p>
                <p className="text-sm text-gray-600">
                  Update your password to keep your account secure
                </p>
              </div>
              <Button variant="primary" onClick={openPasswordModal}>
                Change Password
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Password Modal */}
      {isPasswordModalOpen && (
        <UpdatePasswordModal
          isOpen={isPasswordModalOpen}
          closeModal={closePasswordModal}
        />
      )}
    </div>
  );
};

export default Profile;
